"use client";

import { forwardRef, useState } from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { ArrowLeftIcon, ArrowRightIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { buildContentstackImageUrl } from "@/lib/utils";
import { useModal } from "@/components/ModalContext";
import "swiper/css";

// No zoom, so the useful width is the viewport's times its DPR. 1600 covers
// a 430px phone at 3x without asking Contentstack for more than needed.
const LIGHTBOX_MAX_WIDTH = 1600;

// Single source for the image box's height cap: also drives its width (see
// below), so the two can't drift out of sync.
const LIGHTBOX_MAX_HEIGHT_VH = 70;

// Desktop-only: on mobile, swipe is the only way to navigate.
const NAV_BUTTON_POSITION = "hidden lg:flex absolute top-1/2 z-10 -translate-y-1/2";

const LIGHTBOX_BUTTON_CLASS =
  "w-[32.5px] h-[32.5px] shrink-0 flex items-center justify-center transition-all rounded-full bg-primary text-white hover:scale-105 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer";

// Lightbox-only controls, kept separate from NavButton (used by the main
// carousel)
const LightboxNavButton = ({ direction, onClick, disabled, className = "" }) => {
  const Icon = direction === "prev" ? ArrowLeftIcon : ArrowRightIcon;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`${LIGHTBOX_BUTTON_CLASS} ${className}`}
      aria-label={direction === "prev" ? "Previous image" : "Next image"}
    >
      <Icon className="w-4.5 h-4.5" strokeWidth={2.25} />
    </button>
  );
};

const LightboxCloseButton = forwardRef(({ onClick, className = "" }, ref) => (
  <button
    type="button"
    ref={ref}
    onClick={onClick}
    aria-label="Close modal"
    className={`${LIGHTBOX_BUTTON_CLASS} ${className}`}
  >
    <XMarkIcon className="w-4.5 h-4.5" strokeWidth={2.25} />
  </button>
));
LightboxCloseButton.displayName = "LightboxCloseButton";

const GalleryLightbox = ({ images, initialIndex = 0, onIndexChange }) => {
  const [swiper, setSwiper] = useState(null);
  const [active, setActive] = useState(initialIndex);
  const { closeModal } = useModal();

  const syncSlide = (instance) => {
    setActive(instance.activeIndex);
    onIndexChange?.(instance.activeIndex);
  };


  return (
    // Closes on any click that isn't explicitly stopped, instead of checking
    // e.target === e.currentTarget: content that shouldn't close the modal
    // (the image, the nav buttons) calls stopPropagation itself.
    <div
      className="relative flex h-dvh w-full flex-col items-center justify-center"
      onClick={closeModal}
    >
      {/* Placed first so ModalContext's focus-on-open fallback (first
          focusable element, since `hideDefaultCloseButton` is set) lands
          here instead of on a nav arrow. */}
      <LightboxCloseButton
        onClick={(e) => {
          e.stopPropagation();
          closeModal();
        }}
        className="absolute top-4 right-4 z-20"
      />

      {/* Nav buttons anchor to this wrapper, not the viewport, so top-1/2
          centers on the image itself rather than the full screen height. */}
      <div className="relative w-full">
        <Swiper
          slidesPerView={1}
          initialSlide={initialIndex}
          autoHeight
          onSwiper={setSwiper}
          onSlideChange={syncSlide}
          className="w-full"
        >
          {images.map((image, index) => (
            <SwiperSlide key={image.uid || index}>
              <div className="relative w-full">
                {/* object-contain, not object-cover: shows the whole image, no crop.
                    Width is derived from the ratio (not w-full) so the box's shape
                    stays true to the image once maxHeight caps it. Otherwise the
                    box stays full-width and object-contain letterboxes inside it. */}
                <div
                  className="relative mx-auto"
                  style={{
                    aspectRatio: image.width && image.height ? `${image.width} / ${image.height}` : "4 / 3",
                    maxHeight: `${LIGHTBOX_MAX_HEIGHT_VH}dvh`,
                    width: `min(100%, calc(${LIGHTBOX_MAX_HEIGHT_VH}dvh * ${
                      image.width && image.height ? image.width / image.height : 4 / 3
                    }))`,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Image
                    src={buildContentstackImageUrl(image.url, LIGHTBOX_MAX_WIDTH)}
                    alt={image.alt || `Gallery image ${index + 1}`}
                    fill
                    sizes="100vw"
                    className="object-contain"
                    loading={index === initialIndex ? "eager" : "lazy"}
                  />
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        <LightboxNavButton
          direction="prev"
          onClick={(e) => {
            e.stopPropagation();
            swiper?.slidePrev();
          }}
          disabled={active === 0}
          className={`${NAV_BUTTON_POSITION} left-4`}
        />
        <LightboxNavButton
          direction="next"
          onClick={(e) => {
            e.stopPropagation();
            swiper?.slideNext();
          }}
          disabled={active === images.length - 1}
          className={`${NAV_BUTTON_POSITION} right-4`}
        />
      </div>

      {/* Same progress bar as the mobile carousel, right below the image
          instead of pinned to the viewport bottom. */}
      <div className="w-full px-5 pt-6">
        <div className="relative w-full h-1 bg-sand rounded-full overflow-hidden">
          <div
            className="absolute top-0 left-0 h-full bg-sea rounded-full transition-transform duration-300 ease-out"
            style={{
              width: `${100 / images.length}%`,
              transform: `translate3d(${active * 100}%, 0, 0)`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default GalleryLightbox;
